import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Copy, MonitorSmartphone, Play, QrCode } from 'lucide-react';
import qrcode from 'qrcode-generator';
import { api } from '../../lib/api';
import { deviceId } from '../../lib/device';
import { clock } from '../../lib/format';
import { toast } from '../../lib/session';
import { useRoomSync } from '../../sync/roomStore';
import { joinRoom } from '../room/session';
import { ModalTitle } from '../../ui';

interface Session {
  room: string;
  roomName: string;
  title: string;
  deviceId: string;
  device: string;
  position: number;
  status: string;
}

/** "You're watching on your iPhone — continue here", like moving a call between devices. */
export function DeviceBanner() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const nav = useNavigate();
  const loc = useLocation();
  const roomHere = useRoomSync((s) => (s.status === 'open' ? s.roomId : null));
  useEffect(() => {
    let stop = false;
    const load = () => {
      if (document.visibilityState === 'hidden') return;
      api
        .mySessions()
        .then((r) => !stop && setSessions((r as Session[] | null) ?? []))
        .catch(() => undefined);
    };
    load();
    const t = setInterval(load, 8000);
    const vis = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', vis);
    return () => {
      stop = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', vis);
    };
  }, [loc.pathname]);
  const mine = deviceId();
  const other = sessions.find((s) => s.deviceId !== mine && s.room !== roomHere);
  if (!other) return null;
  return (
    <div className="device-banner" role="status">
      <MonitorSmartphone size={18} style={{ flex: 'none', color: 'var(--accent)' }} />
      <div className="db-text">
        {other.title ? (
          <>
            You're watching <b>{other.title}</b> on {other.device || 'another device'}
          </>
        ) : (
          <>
            You're in <b>{other.roomName || 'a room'}</b> on {other.device || 'another device'}
          </>
        )}
        <small>
          {other.status === 'playing' ? 'Playing' : 'Paused'} at {clock(other.position)} · it moves here and stops there
        </small>
      </div>
      <button className="btn btn-accent btn-sm" onClick={() => joinRoom(other.room, nav)}>
        <Play size={13} fill="currentColor" /> Continue here
      </button>
    </div>
  );
}

function QR({ text }: { text: string }) {
  const cells = useMemo(() => {
    const q = qrcode(0, 'M');
    q.addData(text);
    q.make();
    const n = q.getModuleCount();
    const rects: string[] = [];
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (q.isDark(r, c)) rects.push(`M${c} ${r}h1v1h-1z`);
    return { n, d: rects.join('') };
  }, [text]);
  return (
    <svg className="qr" viewBox={`-2 -2 ${cells.n + 4} ${cells.n + 4}`} role="img" aria-label="QR code to sign in on another device">
      <rect x="-2" y="-2" width={cells.n + 4} height={cells.n + 4} fill="#fff" />
      <path d={cells.d} fill="#000" shapeRendering="crispEdges" />
    </svg>
  );
}

/** A single-use, 15-minute sign-in link shown as a QR code: scan with a phone to sign in there. */
export function AddDevice() {
  const [link, setLink] = useState<{ url: string; expiresAt: number } | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.signinLink().then(setLink, (e) => setErr((e as Error).message));
  }, []);
  return (
    <div style={{ textAlign: 'center' }}>
      <ModalTitle style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
        <QrCode size={20} /> Sign in on another device
      </ModalTitle>
      <p className="muted" style={{ fontSize: 13, margin: '0 auto 16px', maxWidth: '40ch' }}>
        Scan with your phone's camera. It signs that device in as you, once, within 15 minutes. Then playback can move between your devices.
      </p>
      {link ? <QR text={link.url} /> : <div className="qr qr-empty">{err || 'Creating a link…'}</div>}
      {link && (
        <button
          className="btn btn-ghost btn-sm"
          style={{ marginTop: 12 }}
          onClick={() =>
            navigator.clipboard
              .writeText(link.url)
              .then(() => toast('Link copied. Open it on the other device', 'accent'))
              .catch((e) => toast((e as Error).message, 'red'))
          }
        >
          <Copy size={13} /> Copy link instead
        </button>
      )}
    </div>
  );
}
